import { useState, type FormEvent } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  AuthCard,
  FormField,
  PasswordInput,
  VerificationCodeInput,
  SubmitButton,
  Button,
} from '@legacyorb/ui';
import { Input } from '@/components/ui/input';

type Step = 'email' | 'code' | 'password';

export function ForgotPasswordPage() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSendCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setError('');
    setIsSubmitting(true);

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setStep('code');
    } catch (err: unknown) {
      const clerkError = err as { errors?: Array<{ message: string }> };
      const message = clerkError.errors?.[0]?.message || 'Failed to send reset code.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setStep('password');
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate('/dashboard');
      } else {
        setError('Password reset could not be completed. Please try again.');
      }
    } catch (err: unknown) {
      const clerkError = err as { errors?: Array<{ code?: string; message: string }> };
      const message = clerkError.errors?.[0]?.message || 'Failed to reset password.';
      setError(message);
      if (clerkError.errors?.[0]?.code === 'form_code_incorrect') {
        setStep('code');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 'password') {
      setStep('code');
    } else if (step === 'code') {
      setStep('email');
    } else {
      navigate('/sign-in');
    }
  };

  return (
    <div className="auth-theme flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md px-4">
        <div className="mb-8 text-center">
          <h1 className="font-display text-4xl font-bold tracking-tight text-foreground">
            Reset Password
          </h1>
          <p className="mt-2 text-muted-foreground">Reset your password</p>
        </div>

        {step === 'password' ? (
          <AuthCard title="New Password" description="Create a new password for your account">
            <form onSubmit={handleResetPassword} className="space-y-6">
              <FormField label="New Password" htmlFor="password">
                <PasswordInput
                  id="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </FormField>

              <FormField label="Confirm Password" htmlFor="confirmPassword">
                <PasswordInput
                  id="confirmPassword"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </FormField>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <SubmitButton
                variant="brand"
                size="lg"
                isSubmitting={isSubmitting}
                loadingText="Resetting..."
                disabled={!password || !confirmPassword}
              >
                Reset Password
              </SubmitButton>

              <Button type="button" variant="outline" className="w-full" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </form>
          </AuthCard>
        ) : step === 'code' ? (
          <AuthCard title="Enter Code" description={`Enter the 6-digit code sent to ${email}`}>
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <FormField label="Verification Code" htmlFor="code">
                <VerificationCodeInput
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </FormField>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <SubmitButton variant="brand" size="lg" disabled={code.length !== 6}>
                Verify Code
              </SubmitButton>

              <Button type="button" variant="outline" className="w-full" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </form>
          </AuthCard>
        ) : (
          <AuthCard title="Reset Password" description="Enter your email to receive a reset code">
            <form onSubmit={handleSendCode} className="space-y-6">
              <FormField label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </FormField>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <SubmitButton
                variant="brand"
                size="lg"
                isSubmitting={isSubmitting}
                loadingText="Sending code..."
                disabled={!isLoaded || !email}
              >
                Send Reset Code
              </SubmitButton>

              <p className="text-center text-sm text-muted-foreground">
                Remember your password?{' '}
                <Link to="/sign-in" className="text-foreground underline-offset-4 hover:underline">
                  Sign In
                </Link>
              </p>
            </form>
          </AuthCard>
        )}
      </div>
    </div>
  );
}
